'use client';

import { useState, useEffect } from 'react';
import styles from './MobileMenu.module.css';

interface MobileMenuProps {
    children: React.ReactNode;
}

export default function MobileMenu({ children }: MobileMenuProps) {
    const [isOpen, setIsOpen] = useState(false);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const closeMenu = () => {
        setIsOpen(false);
    };

    // Close menu on ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeMenu();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close menu on resize to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                closeMenu();
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            {/* Mobile Menu Toggle Button */}
            <button
                className={`${styles.menuToggle} ${isOpen ? styles.active : ''}`}
                onClick={toggleMenu}
                aria-label="Toggle menu"
                aria-expanded={isOpen}
            >
                <span>{isOpen ? '✕' : '☰'}</span>
            </button>

            {/* Overlay */}
            <div
                className={`${styles.overlay} ${isOpen ? styles.active : ''}`}
                onClick={closeMenu}
                aria-hidden="true"
            />

            {/* Sidebar */}
            <aside
                className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}
                onClick={(e) => {
                    // Close menu when clicking on nav links
                    const target = e.target as HTMLElement;
                    if (target.tagName === 'A') {
                        setTimeout(closeMenu, 100);
                    }
                }}
            >
                {children}
            </aside>
        </>
    );
}
